/**
 * Canonical Labels — mirrors backend/services/canonical_labels.py.
 *
 * Per /app/memory/DATA_CONSISTENCY_LAW.md:
 *   The same heading must never show two different labels anywhere in the app.
 *
 * On app boot we fetch the live dictionary from `/api/labels` and merge it
 * over these defaults. Components should always use the helpers
 * (`retailerName`, `statusLabel`, `fuelType`, `amenityLabel`, `brandName`,
 * `formatGbp`, `formatPence`, `formatDistanceKm`, `timeAgo`) instead of
 * hand-coded capitalisations.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  retailers: {
    tesco: 'Tesco',
    sainsburys: "Sainsbury's",
    asda: 'Asda',
    morrisons: 'Morrisons',
    aldi: 'Aldi',
    lidl: 'Lidl',
    waitrose: 'Waitrose',
    coop: 'Co-op',
    iceland: 'Iceland',
    ocado: 'Ocado',
    'm&s': 'M&S',
    amazon: 'Amazon',
  },
  statuses: {
    pending: 'Pending review',
    approved: 'Approved',
    auto_approved: 'Auto-approved',
    manually_approved: 'Approved by admin',
    rejected: 'Rejected',
    flagged: 'Flagged for review',
    active: 'Active',
    cancelled: 'Cancelled',
  },
  tiers: {
    free: 'Free',
    pro_trial: 'Premium (trial)',
    alpha_tester: 'Alpha Tester',
    early_adopter: 'Early Adopter',
    vip: 'VIP',
    pro_monthly: 'Premium (monthly)',
    pro_yearly: 'Premium (annual)',
    family_monthly: 'Family (monthly)',
    family_yearly: 'Family (annual)',
  },
  fuel_types: {
    e10: 'Unleaded (E10)',
    e5: 'Premium Unleaded (E5)',
    b7: 'Diesel (B7)',
    sdv: 'Super Diesel',
    lpg: 'LPG',
  },
  amenities: {
    adblue_packaged: 'AdBlue (bottle)',
    adblue_pumps: 'AdBlue pump',
    water_filling: 'Air & water',
    customer_toilets: 'Toilets',
    car_wash: 'Car wash',
    lpg_pumps: 'LPG',
    hgv_lanes: 'HGV',
    ev_charging: 'EV charging',
    shop: 'Shop',
    atm: 'ATM',
    jet_wash: 'Jet wash',
  },
  hidden_amenities: ['adblue_packaged'],
  brands: {
    bp: 'BP',
    shell: 'Shell',
    esso: 'Esso',
    texaco: 'Texaco',
    tesco: 'Tesco',
    sainsburys: "Sainsbury's",
    asda: 'Asda',
    morrisons: 'Morrisons',
    coop: 'Co-op',
    jet: 'JET',
    applegreen: 'Applegreen',
    eg_on_the_move: 'EG On The Move',
    mfg: 'Motor Fuel Group',
    motor_fuel_group: 'Motor Fuel Group',
    motorfuelgroup: 'Motor Fuel Group',
    valero: 'Valero',
    rontec: 'Rontec',
  },
};

let CACHE = { ...DEFAULTS };
let loaded = false;
let loadPromise = null;

export async function loadCanonicalLabels() {
  if (loaded) return CACHE;
  if (loadPromise) return loadPromise;
  loadPromise = axios
    .get(`${API}/api/labels`, { timeout: 5000 })
    .then((res) => {
      if (res?.data) {
        CACHE = {
          retailers: { ...DEFAULTS.retailers, ...(res.data.retailers || {}) },
          statuses: { ...DEFAULTS.statuses, ...(res.data.statuses || {}) },
          tiers: { ...DEFAULTS.tiers, ...(res.data.tiers || {}) },
          fuel_types: { ...DEFAULTS.fuel_types, ...(res.data.fuel_types || {}) },
          amenities: { ...DEFAULTS.amenities, ...(res.data.amenities || {}) },
          hidden_amenities: res.data.hidden_amenities || DEFAULTS.hidden_amenities,
          brands: { ...DEFAULTS.brands, ...(res.data.brands || {}) },
        };
      }
      loaded = true;
      return CACHE;
    })
    .catch(() => {
      loaded = true;
      return CACHE;
    });
  return loadPromise;
}

const _titleCase = (s) =>
  (s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function retailerName(key) {
  if (!key) return '';
  const k = String(key).trim().toLowerCase();
  return CACHE.retailers[k] || _titleCase(key);
}

export function statusLabel(key) {
  if (!key) return '';
  const k = String(key).trim().toLowerCase();
  return CACHE.statuses[k] || _titleCase(key);
}

export function tierLabel(key) {
  if (!key) return 'Free';
  const k = String(key).trim().toLowerCase();
  return CACHE.tiers[k] || _titleCase(key);
}

export function fuelType(key) {
  if (!key) return '';
  const k = String(key).trim().toLowerCase();
  return CACHE.fuel_types[k] || _titleCase(key);
}

export function amenityLabel(slug) {
  if (!slug) return '';
  const k = String(slug).trim().toLowerCase();
  return CACHE.amenities[k] || _titleCase(slug);
}

export function visibleAmenities(slugs) {
  if (!Array.isArray(slugs)) return [];
  const hidden = new Set(CACHE.hidden_amenities || []);
  return slugs
    .filter((s) => typeof s === 'string')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => !hidden.has(s))
    .map(amenityLabel);
}

const ALLCAPS = new Set(['BP', 'JET', 'MFG', 'EG', 'LPG', 'HGV']);

export function brandName(key) {
  if (!key) return '';
  const k = String(key).trim().toLowerCase();
  if (CACHE.brands[k]) return CACHE.brands[k];
  return k
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => (ALLCAPS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// ─── Formatters ──────────────────────────────────────────────────────────

export function formatGbp(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return (n < 0 ? '-£' : '£') + Math.abs(n).toFixed(2);
}

export function formatPence(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}p`;
}

export function formatDistanceKm(km) {
  const n = Number(km);
  if (!Number.isFinite(n)) return '—';
  return n < 10 ? `${n.toFixed(1)} km` : `${Math.round(n)} km`;
}

export function timeAgo(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const delta = (Date.now() - d.getTime()) / 1000;
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  if (delta < 86400 * 30) return `${Math.floor(delta / 86400)}d ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
